import React from "react";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";

interface Props {
  totalSteps: number;
  currentStep: number;
  stepLabels?: string[];
  stepTitles?: string[];
  stepDescriptions?: string[];
}

const Steps = ({
  totalSteps,
  currentStep,
  stepLabels = [],
  stepTitles = [],
  stepDescriptions = [],
}: Props) => {
  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "active";
    return "pending";
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "32px 24px",
        backgroundColor: Colors.White,
        borderBottom: `1px solid ${Colors.LightGrey}`,
        marginBottom: "32px",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "12px",
          width: "100%",
          alignItems: "center",
        }}
      >
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const status = getStepStatus(stepNumber);
          const isCompleted = status === "completed";
          const isActive = status === "active";

          return (
            <React.Fragment key={stepNumber}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                  flex: 1,
                }}
              >
                {/* Step Circle */}
                <div
                  style={{
                    position: "relative",
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: isCompleted
                      ? Colors.White
                      : isActive
                        ? Colors.White
                        : Colors.VeryLightGrey,
                    border: isCompleted
                      ? `2px solid ${Colors.TangerineOrange}`
                      : isActive
                        ? `3px solid ${Colors.TangerineOrange}`
                        : `2px solid ${Colors.LightGrey}`,
                    transition: "all 0.4s ease",
                    boxShadow: isActive
                      ? `0 0 0 4px ${Colors.TangerineOrange}20`
                      : "none",
                    transform: isActive ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {isCompleted ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                        fill={Colors.TangerineOrange}
                      />
                    </svg>
                  ) : (
                    <span
                      style={{
                        fontFamily: FontFamily.POPPINS_REGULAR,
                        fontSize: "15px",
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? Colors.TangerineOrange : Colors.Grey,
                      }}
                    >
                      {stepNumber}
                    </span>
                  )}
                </div>

                {/* Step Info Container */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: "150px",
                  }}
                >
                  {/* Step Title */}
                  {stepTitles[index] && (
                    <div
                      style={{
                        fontFamily: FontFamily.POPPINS_REGULAR,
                        fontSize: "13px",
                        fontWeight: isActive ? 600 : 500,
                        color: isActive
                          ? Colors.TangerineOrange
                          : isCompleted
                            ? Colors.Black
                            : Colors.DarkGrey,
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        lineHeight: "1.3",
                      }}
                    >
                      {stepTitles[index]}
                    </div>
                  )}

                  {/* Step Description */}
                  {stepDescriptions[index] && (
                    <div
                      style={{
                        fontFamily: FontFamily.ROBOTO_REGULAR,
                        fontSize: "11px",
                        fontWeight: 400,
                        color: Colors.Grey,
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        lineHeight: "1.4",
                      }}
                    >
                      {stepDescriptions[index]}
                    </div>
                  )}

                  {/* Step Label (legacy support) */}
                  {!stepTitles[index] && stepLabels[index] && (
                    <span
                      style={{
                        fontFamily: FontFamily.POPPINS_REGULAR,
                        fontSize: "12px",
                        fontWeight: isActive ? 500 : 400,
                        color: isActive
                          ? Colors.TangerineOrange
                          : isCompleted
                            ? Colors.DarkGrey
                            : Colors.Grey,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {stepLabels[index]}
                    </span>
                  )}
                </div>
              </div>

              {/* Connector Line */}
              {stepNumber !== totalSteps && (
                <div
                  style={{
                    flex: 1,
                    height: "2px",
                    borderRadius: "10px",
                    backgroundColor:
                      stepNumber < currentStep ? Colors.Grey : Colors.LightGrey,
                    transition: "background-color 0.6s ease",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Steps;
